import { type Key, useCallback, useMemo, useState } from 'react';
import difference from 'lodash/difference';
import uniq from 'lodash/uniq';
import without from 'lodash/without';
import type {
  RowSelectMethod,
  TableRowSelection,
} from '@timeedit/ui-components/antd/table/interface';

type IdSelectionMode = 'select' | 'deselect';

/**
 * Hook for managing selection in an infinitely scrolling list
 * Handles special cases for `select all`, so that items can be selected without having to know their ID's.
 */
export function useTableSelection<T extends { id: number | string }>(
  items: T[],
  onRowSelect?: (items: T[]) => void,
  enabled: boolean = true
) {
  type IdType = T['id'];

  const [excludedIds, setExcludedIds] = useState<IdType[]>([]);
  const [includedIds, setIncludedIds] = useState<IdType[]>([]);
  const [idSelectionMode, setIdSelectionMode] =
    useState<IdSelectionMode>('select');

  const [allSelected, setAllSelected] = useState(false);

  const selectedRowIds = useMemo<IdType[]>(() => {
    if (idSelectionMode === 'select') {
      return includedIds;
    }

    const allIds = addAllKeys(items).filter(Boolean) as IdType[];

    if (excludedIds.length) {
      return withRemovedIds(excludedIds)(allIds);
    }

    return allIds;
  }, [allSelected, idSelectionMode, includedIds, excludedIds, items]);

  const handleAllSelection = useCallback(
    (type: RowSelectMethod, selectedRowKeys: Key[]) => {
      if (type === 'all') {
        setIdSelectionMode(selectedRowKeys.length ? 'deselect' : 'select');
        setIncludedIds([]);
        setExcludedIds([]);
        setAllSelected((prev) => !prev);
      } else {
        setAllSelected(false);
      }
    },
    []
  );

  const handleRowSelectionChange = useCallback<
    Exclude<TableRowSelection<T>['onChange'], undefined>
  >(
    (selectedRowKeys, selectedRows, info) => {
      handleAllSelection(info.type, selectedRowKeys);
      onRowSelect?.(selectedRows);
    },
    [handleAllSelection, onRowSelect]
  );

  const handleRowSelection = useCallback<
    Exclude<TableRowSelection<T>['onSelect'], undefined>
  >(
    (record, selected) => {
      if (idSelectionMode === 'select') {
        if (selected) {
          setIncludedIds(withAddedIds(record.id));
        } else {
          setIncludedIds(withRemovedId(record.id));
        }
      } else {
        if (selected) {
          setExcludedIds(withRemovedId(record.id));
        } else {
          setExcludedIds(withAddedIds(record.id));
        }
      }
    },
    [idSelectionMode]
  );

  const handleRowMultipleSelection = useCallback<
    Exclude<TableRowSelection<T>['onSelectMultiple'], undefined>
  >(
    (selected, _, changeRows) => {
      const ids = changeRows.map((row) => row.id);

      if (idSelectionMode === 'select') {
        if (selected) {
          setIncludedIds(withAddedIds(ids));
        } else {
          setIncludedIds(withRemovedIds(ids));
        }
      } else {
        if (selected) {
          setExcludedIds(withRemovedIds(ids));
        } else {
          setExcludedIds(withAddedIds(ids));
        }
      }
    },
    [idSelectionMode]
  );

  const clearSelection = useCallback(() => {
    setIdSelectionMode('select');
    setIncludedIds([]);
    setExcludedIds([]);
  }, []);

  const rowSelection = useMemo<TableRowSelection<T> | undefined>(() => {
    if (!enabled) return undefined;
    return {
      columnWidth: 32,
      getCheckboxProps: (record) => ({
        name: record.id.toString(),
      }),
      onChange: handleRowSelectionChange,
      onSelect: handleRowSelection,
      onSelectMultiple: handleRowMultipleSelection,
      selectedRowKeys: selectedRowIds,
    };
  }, [
    enabled,
    handleRowMultipleSelection,
    handleRowSelection,
    handleRowSelectionChange,
    selectedRowIds,
  ]);

  return {
    enabled,
    idSelectionMode,
    includedIds,
    excludedIds,
    allSelected,
    rowSelection,
    clearSelection,
  };
}

const addAllKeys = <IdType extends string | number>(
  list: { id: IdType }[] = []
): Key[] => list.map((v) => v.id as Key);

const withAddedIds =
  <IdType extends string | number>(id: IdType | IdType[]) =>
  (ids: IdType[]) =>
    uniq(ids.concat(id));

const withRemovedId =
  <IdType extends string | number>(id: IdType) =>
  (ids: IdType[]) =>
    uniq(without(ids, id));

const withRemovedIds =
  <IdType extends string | number>(removedIds: IdType[]) =>
  (ids: IdType[]) =>
    uniq(difference(ids, removedIds));
